/**
 * The SAMPLE seed — the [LIVE]-lock source for the smart editors' `seed` prop
 * (extracted from the deleted PassDesignEditor; the canvas host AppleStudio
 * consumes it now). Mirrors the server GET-one seed path EXACTLY:
 * serviceToDesign(sampleServiceFor(kind), SAMPLE_FLIGHT, style), de-lounged
 * for the GENERAL template via generalizeAssistanceSeed
 * (partner-portal-api: services/passDesignSamples.ts + assistanceContent.ts).
 * The per-kind sample fixtures + composition live in ONE mirrored place —
 * src/api/mock.ts — so the [LIVE]-lock seed cannot drift from the mock
 * GET-one seed (a kind-blind seed here would lock the WRONG values for
 * assistance designs).
 *
 * The lock derivation must run on THIS seed, never on the persisted design
 * (seedLiveKeys.ts contract): a reloaded saved row is operator-authored, and
 * deriving locks from it would freeze EVERY field the operator ever saved.
 */

import type { PassDesign } from '@insurwiz/wallet-core';

import { designSeedFor } from '../../api/mock';
import type { PassTemplateKind } from '../../types';
import type { AppleStyle } from '../../walletStyles';

const sampleSeedCache = new Map<string, PassDesign>();

/** Client-side sample seed, cached per (kind, style) so the hosts get a
 *  referentially stable object per selection. `designSeedFor` is the one
 *  shared composition (serviceToDesign + the GENERAL template's de-lounging)
 *  the mock GET-one seed uses too — the [LIVE] locks cannot drift from it. */
export function sampleSeedFor(templateKind: PassTemplateKind, appleStyle: AppleStyle): PassDesign {
  const key = `${templateKind}:${appleStyle}`;
  let seed = sampleSeedCache.get(key);
  if (seed === undefined) {
    seed = designSeedFor(templateKind, appleStyle);
    sampleSeedCache.set(key, seed);
  }
  return seed;
}
